import { motion, useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'
import type { ReportBlock } from '@/lib/types/report'
import { resolveBlock } from './registry'
import { SectionHeader } from './primitives/SectionHeader'
import { FigureNumberContext } from './primitives/FigureNumberContext'

/** Block types that receive a deterministic figure number, in block order. */
const FIGURE_TYPES = new Set(['project_graph', 'attribution_sankey', 'contribution_bar', 'composition_donut'])

export function ReportRenderer({
  blocks,
  changedIds = [],
  contentVersion = 0,
  writingId = null,
  sectionVersions = {},
  showChips = false,
}: {
  blocks: ReportBlock[]
  changedIds?: string[]
  contentVersion?: number
  /** Section id the agent is writing right now — gets the in-place shimmer. */
  writingId?: string | null
  /** Last write version per section (persistent "updated · vN" chips while live). */
  sectionVersions?: Record<string, number>
  showChips?: boolean
}) {
  const reduceMotion = useReducedMotion()
  let figure = 0
  return (
    <>
      {blocks.map((block, i) => {
        const Component = resolveBlock(block.type)
        const figureNumber = FIGURE_TYPES.has(block.type) ? ++figure : null
        const changed = block.id != null && changedIds.includes(block.id)
        const writing = block.id != null && block.id === writingId
        const chipVersion =
          block.id != null && (showChips || changed) ? (sectionVersions[block.id] ?? (changed ? contentVersion : null)) : null
        const baseKey = block.id ? `${block.id}-${i}` : String(i)
        return (
          <motion.section
            // key remount on (changed × contentVersion) deterministically retriggers the
            // CSS flash and the entrance motion; static sections keep a stable key.
            key={changed ? `${baseKey}-v${contentVersion}` : baseKey}
            initial={reduceMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className={cn(
              'report-section relative',
              changed && 'report-section-flash',
              writing && 'report-section-writing',
            )}
            data-section-id={block.id ?? undefined}
          >
            {writing ? (
              <span
                role="status"
                className="report-writing-tag print:hidden absolute right-0 top-0 inline-flex items-center gap-1.5 rounded-full border border-[var(--report-accent)]/40 bg-[var(--report-accent)]/10 px-2 py-0.5 text-xs font-medium text-[var(--report-accent)]"
              >
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--report-accent)]" aria-hidden="true" />
                writing…
              </span>
            ) : (
              chipVersion != null && (
                <span
                  className={cn(
                    'report-version-chip print:hidden absolute right-0 top-0 inline-flex items-center rounded-full border border-[var(--report-accent)]/40 bg-[var(--report-accent)]/10 px-2 py-0.5 text-xs font-medium text-[var(--report-accent)]',
                    showChips && 'report-version-chip-live',
                  )}
                >
                  updated · v{chipVersion}
                </span>
              )
            )}
            {block.title && block.type !== 'pending_section' && <SectionHeader title={block.title} />}
            <FigureNumberContext.Provider value={figureNumber}>
              <Component block={block} />
            </FigureNumberContext.Provider>
          </motion.section>
        )
      })}
    </>
  )
}
